import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Pagination from '../Pagination';
import Spinner from '../ui/Spinner';
import { getExpenseApprovals, getDriverExpenses } from '../../services/driver_expenses';
import { formatDMY } from '../../utils/datetime';

const DriverExpenseTable = ({
  month,
  year,
  status,
  selectedDriver,
  onCountUpdate,
  onEdit,
  onDelete,
  reload,
}) => {
  const [expenses, setExpenses] = useState([]);
  const [expenseCount, setExpenseCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const expensesPerPage = 10;

  useEffect(() => {
    if (selectedDriver) {
      fetchData(1);
      setCurrentPage(1);
    }
  }, [month, year, status, selectedDriver, reload]);

  const fetchData = async page => {
    setLoading(true);
    await Promise.all([fetchExpenses(page), fetchApprovals(1)]);
    setLoading(false);
  };

  const fetchExpenses = async (page = 1) => {
    setLoading(true);
    const [err, data] = await getDriverExpenses({
      driver: selectedDriver,
      status,
      month,
      year,
      page,
    });
    if (!err) {
      setExpenses(data.results);
      setExpenseCount(data.count);
      setCurrentPage(page || 1);
      onCountUpdate('expenses', data.count);
    }
    setLoading(false);
  };

  const fetchApprovals = async page => {
    setLoading(true);
    const [err, data] = await getExpenseApprovals({
      driver: selectedDriver,
      status,
      month,
      year,
      page,
    });
    if (!err) {
      onCountUpdate('approvals', data.count);
    }
    setLoading(false);
  };

  return (
    <>
      <div className="overflow-x-auto mt-4">
        <table className="min-w-full bg-white border">
          <thead className="bg-[#293686] text-white uppercase text-sm">
            <tr>
              <th className="px-4 py-2 border text-center">Date</th>
              <th className="px-4 py-2 border text-center">Charges</th>
              <th className="px-4 py-2 border text-center">Amount</th>
              <th className="px-4 py-2 border text-center">Reason</th>
              <th className="px-4 py-2 border text-center">Remarks</th>
              <th className="px-4 py-2 border text-center">Attachment</th>
              <th className="px-4 py-2 border text-center">Created By</th>
              <th className="px-4 py-2 border text-center">Confirmed By</th>
              <th className="px-4 py-2 border text-center">Status</th>
              <th className="px-4 py-2 border text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  <Spinner />
                </td>
              </tr>
            ) : expenses.length > 0 ? (
              expenses.map(expense => (
                <tr key={expense.id} className="text-sm text-gray-800">
                  <td className="px-4 py-2 border text-center">{formatDMY(expense.date)}</td>
                  <td className="px-4 py-2 border text-center">
                    {expense.expense_type?.toUpperCase()}
                  </td>
                  <td className="px-4 py-2 border text-center">₹{expense.amount}</td>
                  <td className="px-4 py-2 border text-center">{expense.reason}</td>
                  <td className="px-4 py-2 border text-center">{expense.remarks}</td>
                  <td className="px-4 py-2 border text-center">
                    {expense.supporting_doc && (
                      <a
                        href={expense.supporting_doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        VIEW
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {expense.created_by?.name?.toUpperCase()}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {expense.confirmed_by?.name?.toUpperCase() || 'SELF'}
                  </td>
                  <td className="px-4 py-2 border text-center">{expense.status?.toUpperCase()}</td>
                  <td className="px-4 py-2 border text-center">
                    <button
                      onClick={() => onEdit(expense)}
                      className={`p-1 ${
                        expense.status === 'rejected'
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-blue-600 hover:text-blue-800'
                      }`}
                      title="Edit"
                      disabled={expense.status === 'rejected'}
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDelete(expense)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Edit"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-gray-500 py-3">
                  NO EXPENSES FOUND FOR THIS MONTH.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {expenses.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.max(1, Math.ceil(expenseCount / expensesPerPage))}
          onPageChange={setPendingPage}
        />
      )}
    </>
  );
};

export default DriverExpenseTable;
